/**
 * Image Role Definitions
 * =======================
 * Single source of truth for prompt image roles.
 * Adding a new role = adding to IMAGE_ROLES + ROLE_CYCLE_ORDER.
 *
 * Each role controls:
 *  - UI badge appearance (className, icon identifier)
 *  - Vision analysis focus (analysisPrompt, analysisMaxTokens)
 *  - Prompt injection clause (instructionTemplate)
 */

export const IMAGE_ROLES = {
  subject: {
    id: 'subject',
    label: 'Subject',
    shortLabel: 'Subj',
    description: 'Use the subject/object from this image',
    className: 'prompt-chip--subject',
    icon: 'subject',
    analysisPrompt:
      'Briefly describe the main subject/object in 5-10 words: what it is, key visual properties (color, texture, material), pose or orientation if relevant. Be factual and concise.',
    analysisMaxTokens: 30,
    instructionTemplate: 'using the subject from {refs}',
  },
  style: {
    id: 'style',
    label: 'Style',
    shortLabel: 'Style',
    description: 'In the visual style of this image',
    className: 'prompt-chip--style',
    icon: 'style',
    analysisPrompt:
      'Describe VISUAL STYLE only in 10-20 words: artistic technique, color palette, lighting mood, texture quality, overall aesthetic. Do NOT describe subject matter.',
    analysisMaxTokens: 60,
    instructionTemplate: 'in the visual style of {refs}',
  },
  scene: {
    id: 'scene',
    label: 'Scene',
    shortLabel: 'Scene',
    description: 'In the setting/environment of this image',
    className: 'prompt-chip--scene',
    icon: 'scene',
    analysisPrompt:
      'Describe the environment/setting in 5-10 words: location type, lighting conditions, atmosphere, key environmental features. Be factual and concise.',
    analysisMaxTokens: 30,
    instructionTemplate: 'set in the environment of {refs}',
  },
  structure: {
    id: 'structure',
    label: 'Structure',
    shortLabel: 'Struct',
    description: 'Preserve the composition/layout/pose from this image',
    className: 'prompt-chip--structure',
    icon: 'structure',
    analysisPrompt:
      'Describe the COMPOSITION and LAYOUT only in 5-10 words: subject placement, camera angle, framing, spatial arrangement, pose if present. Do NOT describe content or style.',
    analysisMaxTokens: 30,
    instructionTemplate: 'preserving the composition and layout of {refs}',
  },
};

/** Ordered list for cycling roles on badge click */
export const ROLE_CYCLE_ORDER = ['subject', 'style', 'scene', 'structure'];

/** Canonical prompt/reference role vocabulary for schemas and model contracts. */
export const IMAGE_ROLE_VALUES = Object.freeze(['subject', 'scene', 'structure', 'style']);

/** Default role assigned when an image is first dropped into the prompt */
export const DEFAULT_ROLE = 'subject';

export function isValidImageRole(value) {
  return typeof value === 'string' && Object.hasOwn(IMAGE_ROLES, value);
}

export function normalizeImageRole(value) {
  return isValidImageRole(value) ? value : DEFAULT_ROLE;
}

export function getNextAllowedImageRole(currentRole, allowedRoles = ROLE_CYCLE_ORDER) {
  const roleCycle = Array.isArray(allowedRoles) && allowedRoles.length > 0
    ? allowedRoles.filter(isValidImageRole)
    : ROLE_CYCLE_ORDER;
  const safeRoleCycle = roleCycle.length > 0 ? roleCycle : ROLE_CYCLE_ORDER;
  const normalizedCurrentRole = isValidImageRole(currentRole) ? currentRole : safeRoleCycle[0];
  const currentIndex = safeRoleCycle.indexOf(normalizedCurrentRole);
  return safeRoleCycle[(currentIndex + 1) % safeRoleCycle.length] || DEFAULT_ROLE;
}

/**
 * Get the next role in the cycle.
 * @param {string} currentRole
 * @returns {string}
 */
export function getNextRole(currentRole) {
  return getNextAllowedImageRole(currentRole, ROLE_CYCLE_ORDER);
}
